import type { LeadRequest } from "@component/assets/AbstractLeadForm/types";
import { JsonDataBuilder } from "@utils/JsonDataBuilder";
import autobind from "autobind-decorator";
import type { ReactNode } from "react";
import React from "react";
import { FactoringLeadForm } from "../LeadForm/FactoringLeadForm";
import * as Step from "./Steps";
import type {
	FactoringSmallCalculatorFormFields,
	FactoringSmallCalculatorProps,
	FactoringSmallCalculatorRequest,
	FactoringSmallCalculatorResponse,
	FactoringSmallCalculatorResult,
	FactoringSmallCalculatorState,
} from "./types";
import "./style.scss";

const ADVANCES_RATE = 0.9;

/**
 * @Shortcode
 *
 * @title "Faktoring kalkulátor"
 * @shortcode "factoring_small"
 * @slug "faktoring"
 * @type "calculator"
 */
export class FactoringSmallCalculator extends FactoringLeadForm<
	FactoringSmallCalculatorRequest,
	FactoringSmallCalculatorResponse,
	FactoringSmallCalculatorFormFields,
	FactoringSmallCalculatorProps,
	FactoringSmallCalculatorState
> {
	// EventParams
	protected readonly productTypes: string = "FACTORING";
	protected readonly componentType: string = "small-calculator";

	public static defaultProps = {
		title: "Azonnali tájékoztatás a hitel feltételeiről.",
		outsideTitle: "Mennyi finanszírozást kaphatok vevőimre?",
		outsideTitleLink: "/vallalati/hitel/faktoring/",
		bottomLogo: true,
		variant: "small-calculator",
		colorScheme: "dark",
	};

	protected getInitialState(): FactoringSmallCalculatorState {
		return {
			...super.getInitialState(),
			activeStep: "step-1",
		};
	}

	protected renderSteps(): ReactNode {
		const { submitState } = this.state;

		return (
			<>
				{super.renderSteps()}
				<Step.Step1 stepName="step-1" defaultTarget="result" submitState={submitState} />
				<Step.Result stepName="result" defaultTarget="lead-form" canStepBack submitState={submitState} />
			</>
		);
	}

	protected onStep(target: string, withEvent: boolean): void {
		switch (target) {
			case "result":
				this.sendCalculationEvent();
				super.onStep("result", withEvent);
				break;

			default:
				super.onStep(target, withEvent);
		}
	}

	private getFactoringValue(grossIncome: number, duration: number): FactoringSmallCalculatorResult {
		const deposit = Math.round(0.9 * grossIncome);
		const cost = Math.round(grossIncome * duration * 0.00025);
		const remain = Math.max(0, grossIncome - deposit - cost);

		return {
			deposit,
			cost,
			remain,
		};
	}

	@autobind
	protected getLeadData(): LeadRequest<FactoringSmallCalculatorRequest, FactoringSmallCalculatorResponse> {
		const {
			values: { grossIncome, averagePaymentDeadline },
		} = this.state;
		const data = super.getLeadData();
		const { deposit, cost, remain } = this.getFactoringValue(grossIncome, averagePaymentDeadline);

		const otherData = new JsonDataBuilder();
		otherData.addItem("Számla bruttó összege", "grossIncome", grossIncome, "ft");
		otherData.addItem("Átlagos számla fizetési határidő", "averagePaymentDeadline", averagePaymentDeadline, "nap");
		otherData.addItem("Megelőlegezés", "deposit", deposit, "ft");
		otherData.addItem("Költség", "cost", cost, "ft");
		otherData.addItem("Faktormaradvány", "remain", remain, "ft");

		data.request = {
			...data.request,
			grossIncome,
			advancesRate: ADVANCES_RATE,
			averagePaymentDeadline,
			otherDataJson: otherData.getJson(),
		};

		data.response = {
			...data.response,
			factoringAmount: deposit,
		};

		return data;
	}

	protected getClassName(): string {
		return `${super.getClassName()} factoring-small`;
	}

	protected getAutoresponderName(): string {
		return "autoresponder-factoring-calculator";
	}

	protected getComponentName(): string {
		return `${super.getComponentName()}.FactoringSmallCalculator`;
	}
}

export default FactoringSmallCalculator;
